import { gql, type ApolloClient } from '@apollo/client';
import {
  Pagination,
  productFilter,
} from '_graphql/__generated__/globalTypes';
import { normalizeBulkUploadHeader } from './parseBulkProductUpload';

const PAGE_SIZE = 250;

const bulkUploadFetchProductTagsSupplier = gql`
  query BulkUploadFetchProductTagsSupplier(
    $filter: productFilter
    $pagination: Pagination
  ) {
    getProducts(filter: $filter, pagination: $pagination) {
      id
      tags
    }
  }
`;

type BulkUploadFetchProductTagsSupplier = {
  getProducts:
    | ({ id: string; tags: (string | null)[] | null } | null)[]
    | null;
};

type BulkUploadFetchProductTagsSupplierVariables = {
  filter?: productFilter;
  pagination?: Pagination;
};

/**
 * Builds a lookup map: normalized tag label → canonical tag string (first occurrence wins).
 */
export function buildTagNameToCanonical(tagNames: string[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const raw of tagNames) {
    const t = raw.trim();
    if (!t) {
      continue;
    }
    const key = normalizeBulkUploadHeader(t);
    if (!m.has(key)) {
      m.set(key, t);
    }
  }
  return m;
}

/**
 * Loads distinct non-empty product tags for this supplier (optionally filtered by country).
 */
export async function fetchAllProductTags(
  client: ApolloClient<object>,
  params: { supplierId: string; country?: string | null },
): Promise<string[]> {
  const filter: productFilter = {
    supplier_id: { eq: params.supplierId },
  };
  if (params.country) {
    filter.country = { eq: params.country };
  }

  const seen = new Map<string, string>();
  let offset = 0;

  for (;;) {
    const { data } = await client.query<
      BulkUploadFetchProductTagsSupplier,
      BulkUploadFetchProductTagsSupplierVariables
    >({
      query: bulkUploadFetchProductTagsSupplier,
      variables: {
        filter,
        pagination: { limit: PAGE_SIZE, offset },
      },
      fetchPolicy: 'network-only',
    });

    const batch = (data?.getProducts ?? []).filter(
      (p): p is NonNullable<typeof p> => p != null,
    );
    for (const p of batch) {
      for (const t of p.tags ?? []) {
        if (t == null) {
          continue;
        }
        const trimmed = String(t).trim();
        if (!trimmed) {
          continue;
        }
        const key = normalizeBulkUploadHeader(trimmed);
        if (!seen.has(key)) {
          seen.set(key, trimmed);
        }
      }
    }

    if (batch.length < PAGE_SIZE) {
      break;
    }
    offset += PAGE_SIZE;
  }

  return Array.from(seen.values()).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  );
}
